import { Action, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

import * as uploadActions from '../upload/upload.actions';

export enum UploadStatus {
  READY = 'Ready',
  RESET = 'Reset',
  REQUESTED = 'Requested',
  STARTED = 'Started',
  FAILED = 'Failed',
  COMPLETED = 'Completed',
}

export interface Upload {
  id?: string;
  status: UploadStatus;
  error: string | null;
  progress: number | null;
  uploaded: File[] | string[];
}

const initialUploadStateValues: Upload = {
  status: UploadStatus.READY,
  error: null,
  progress: null,
  uploaded: [],
};

export interface UploadState extends EntityState<Upload> {}

export const uploadAdapter: EntityAdapter<Upload> = createEntityAdapter<Upload>();

export const initialUploadState: UploadState = uploadAdapter.getInitialState({});

const reducer = createReducer(
  initialUploadState,
  on(uploadActions.initializeUpload, (state, { uuid }) => {
    return uploadAdapter.addOne(
      { ...initialUploadStateValues, id: uuid },
      {
        ...state,
      }
    );
  }),
  on(uploadActions.uploadRequest, (state, { uuid }) => {
    return uploadAdapter.updateOne(
      {
        id: uuid,
        changes: {
          status: UploadStatus.REQUESTED,
          progress: null,
          error: null,
        },
      },
      {
        ...state,
      }
    );
  }),
  on(uploadActions.uploadCancel, (state, { uuid }) => {
    return uploadAdapter.updateOne(
      {
        id: uuid,
        changes: {
          status: UploadStatus.READY,
          progress: null,
          error: null,
        },
      },
      {
        ...state,
      }
    );
  }),
  on(uploadActions.uploadReset, (state, { uuid }) => {
    return uploadAdapter.updateOne(
      {
        id: uuid,
        changes: {
          status: UploadStatus.RESET,
          progress: null,
          error: null,
          uploaded: [],
        },
      },
      {
        ...state,
      }
    );
  }),
  on(uploadActions.uploadError, (state, { uuid, error }) => {
    return uploadAdapter.updateOne(
      {
        id: uuid,
        changes: {
          status: UploadStatus.FAILED,
          progress: null,
          error,
        },
      },
      {
        ...state,
      }
    );
  }),
  on(uploadActions.uploadStarted, (state, { uuid }) => {
    return uploadAdapter.updateOne(
      {
        id: uuid,
        changes: {
          status: UploadStatus.STARTED,
          progress: 0,
        },
      },
      {
        ...state,
      }
    );
  }),
  on(uploadActions.uploadProgress, (state, { uuid, progress }) => {
    return uploadAdapter.updateOne(
      {
        id: uuid,
        changes: {
          progress,
        },
      },
      {
        ...state,
      }
    );
  }),
  on(uploadActions.uploadCompleted, (state, { uuid, uploaded }) => {
    return uploadAdapter.updateOne(
      {
        id: uuid,
        changes: {
          status: UploadStatus.COMPLETED,
          progress: 100,
          error: null,
          uploaded,
        },
      },
      {
        ...state,
      }
    );
  })
);

export function uploadReducer(state: UploadState | undefined, action: Action) {
  return reducer(state, action);
}
